import React from 'react';
import PropTypes from 'prop-types';
import './QrStyleForm.css';
import Select2 from './Select2.jsx';
import CsrfToken from './shared/CsrfToken';
import ColorsTab from './qrStyle/ColorsTab.jsx';
import ShapesTab from './qrStyle/ShapesTab.jsx';
import LogoTab from './qrStyle/LogoTab.jsx';
import QrPreview from './qrStyle/QrPreview.jsx';
import styleHiddenInputs from './qrStyle/styleHiddenInputs.jsx';
import FieldErrors, { fieldClass } from './qrStyle/FieldErrors.jsx';
import QrGeneralErrors from './qrStyle/QrGeneralErrors.jsx';
import { normalizeErrors, locationForKey } from './qrStyle/styleErrorTabs.jsx';

const TABS = [
  { key: 'colors', label: 'Colors' },
  { key: 'shapes', label: 'Shapes' },
  { key: 'logo', label: 'Logo' },
];

// Stable reference: a new object each render makes Select2 re-init on every keystroke.
const SELECT2_OPTIONS = { minimumResultsForSearch: Infinity };

class QrStyleForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      style: { ...props.initial_style },
      name: props.name || '',
      size: props.default_download_size || props.sizes[0],
      activeTab: 'colors',
      logoSignedId: null,
      logoFileName: null,
      uploadingLogo: false,
      logoUploadError: false,
      removeLogo: false,
      errors: normalizeErrors(props.initial_errors),
    };
    // Built once. A fresh array each render makes Select2 think its data changed,
    // which re-fires its change event → onChange → setState → infinite loop.
    this.sizeData = (props.sizes || []).map((n) => ({ id: String(n), text: `${n} px` }));
    this.patchStyle = this.patchStyle.bind(this);
    this.handleLogoFile = this.handleLogoFile.bind(this);
    this.handleRemoveLogo = this.handleRemoveLogo.bind(this);
    this.handleClearLogo = this.handleClearLogo.bind(this);
    this.clearError = this.clearError.bind(this);
    // Not state: it gates stale async resolutions and must never trigger a render.
    // fetch cannot be cancelled from here, so an upload is "abandoned" by bumping
    // this — its resolution then finds itself superseded and writes nothing.
    this.uploadSeq = 0;
  }

  // `value` may be a function (prev) => next, which merges into the LATEST state.
  // Nested keys like `gradient` need this: a Select2 change can fire from a handler
  // bound at an earlier render, so reconstructing the gradient from a closure-
  // captured value would clobber edits made since. Reading live state avoids that.
  patchStyle(key, value) {
    this.setState((s) => {
      const errors = { ...s.errors };
      delete errors[key];
      return {
        style: { ...s.style, [key]: typeof value === 'function' ? value(s.style[key]) : value },
        errors,
      };
    });
  }

  clearError(key) {
    this.setState((s) => {
      if (!s.errors[key]) return null;
      const errors = { ...s.errors };
      delete errors[key];
      return { errors };
    });
  }

  // Upload the picked File immediately and hold its signed id. The signed id
  // (not raw bytes) is what gets submitted as qr_code_template[logo] on save.
  handleLogoFile(file) {
    if (!file) {
      // Bump here too: clearing the picker abandons an in-flight upload just as a
      // removal does, and every abandoning transition must supersede its generation
      // or the resolution writes a signed id back behind an empty picker.
      this.uploadSeq += 1;
      this.setState({
        logoSignedId: null, logoFileName: null, uploadingLogo: false, logoUploadError: false,
      });
      return;
    }
    // The name is held from the pick, not the upload: it is what the field shows
    // while the bytes are still going up, and it survives a failed upload so the
    // "pick the file again to retry" note has something to refer to.
    //
    // No `removeLogo: false` here, though the old code had one. A pick cannot land
    // while a removal is staged — the field disables the picker — so withdrawing
    // one would be a branch nothing can take. handleClearLogo is the single route
    // between the two intents, and it discards the pick before it will stage a
    // removal, so the two are never both live to begin with.
    this.setState({
      uploadingLogo: true, logoUploadError: false, logoFileName: file.name,
    });
    this.clearError('logo');
    this.uploadSeq += 1;
    const seq = this.uploadSeq;
    const body = new FormData();
    body.append('file', file);
    fetch(this.props.logo_upload_url, {
      method: 'POST',
      body,
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': this.props.csrf.token, Accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      // Both callbacks land after a round trip, by which time the user may have asked
      // to remove the logo or picked a different file. Either bumps uploadSeq, so this
      // resolution is stale and writes nothing — otherwise it would resurrect a signed
      // id the form would submit alongside the removal flag, or let a slow earlier
      // upload overwrite a newer one.
      .then((data) => {
        if (seq !== this.uploadSeq) return;
        this.setState({ logoSignedId: data.signed_id, uploadingLogo: false });
      })
      .catch(() => {
        if (seq !== this.uploadSeq) return;
        this.setState({ logoSignedId: null, uploadingLogo: false, logoUploadError: true });
      });
  }

  // Staging a removal and holding an unsaved pick are mutually exclusive states, so
  // this only ever flips the flag. handleClearLogo is the single route INTO a staged
  // removal — its other caller, the note's cancel link, calls this with `false` — and
  // it discards a pick before it will stage one, which means a removal is only ever
  // staged with logoFileName already null, and with it uploadingLogo, logoSignedId
  // and logoUploadError, since all three are only set while a pick is live.
  // Abandoning an in-flight upload therefore happens upstream, in
  // handleLogoFile(null), which is where the uploadSeq bump that supersedes a stale
  // resolution lives. Don't add one here: it would guard a state this cannot reach.
  //
  // That guarantee — checked === true having exactly one guarded call site — is a
  // rendering decision (LogoFileField's disabled={removePending} keeps a pick from
  // landing while a removal is staged), so it is not the thing actually stopping the
  // server from acting on both signals. QrCodeTemplatesController#remove_logo_requested?
  // returns false whenever qr_code_template[logo] is present, so even if a client bug
  // ever let both submit, the uploaded logo wins and nothing is destroyed. If you
  // re-enable the picker during a staged removal, the consequence is "the server
  // ignores the flag", not "the form breaks its invariant".
  handleRemoveLogo(checked) {
    this.setState({ removeLogo: checked });
  }

  // The ✗ acts on whatever the field is showing. An unsaved pick wins over the saved
  // logo there, so the first click discards the pick and lets the saved name come
  // back; only with nothing picked does the icon mean "remove the saved logo". That
  // keeps one click from doing something the user cannot see the reason for, and it
  // is the only way back from a mis-pick now that the native input is hidden.
  handleClearLogo() {
    if (this.state.logoFileName) {
      this.handleLogoFile(null);
    } else {
      this.handleRemoveLogo(true);
    }
  }

  render() {
    const {
      style, name, size, activeTab, logoSignedId, uploadingLogo, logoUploadError,
      logoFileName, removeLogo, errors,
    } = this.state;
    const p = this.props;
    // Every tab panel stays mounted (only the active one is visible) so the Logo
    // tab's file input always submits, even when another tab is showing.
    const panel = (key) => `qr-tabpanel${activeTab === key ? '' : ' qr-hidden'}`;
    const errorLocations = new Set(Object.keys(errors).map(locationForKey));
    return (
      <div className="qr-style-form">
        <div className="qr-form-layout">
          <div className="qr-form-col">
            <form action={p.submit_url} method="post" acceptCharset="UTF-8" encType="multipart/form-data" noValidate>
              <CsrfToken csrf={p.csrf} />
              {p.http_method === 'patch' && <input type="hidden" name="_method" value="patch" readOnly />}

              <QrGeneralErrors errors={errors} />

              <div className="qr-section-label">General</div>
              <div className="qr-card">
                <div className={fieldClass(errors, 'name')}>
                  <label className="qr-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="qr_code_template[name]"
                    placeholder="My Custom Style"
                    value={name}
                    onChange={(e) => { this.setState({ name: e.target.value }); this.clearError('name'); }}
                  />
                  <FieldErrors messages={errors.name} />
                </div>
                <div className={fieldClass(errors, 'default_download_size')}>
                  <label className="qr-label">Default download size</label>
                  <Select2
                    className="form-control"
                    name="qr_code_template[default_download_size]"
                    data-testid="download-size"
                    value={String(size)}
                    data={this.sizeData}
                    options={SELECT2_OPTIONS}
                    onChange={(e) => {
                      // A rejected save re-renders with the size the user submitted, which
                      // may be none of the offered options. The <select> then matches
                      // nothing and reports '' — and Select2 re-fires change on every
                      // re-render after the first. parseInt('') is NaN, and NaN !== NaN,
                      // so storing it makes every later render look like a state change:
                      // an infinite update loop. Ignore the event and keep the submitted
                      // value on screen, so its "is not included in the list" error stands.
                      const next = parseInt(e.target.value, 10);
                      if (Number.isNaN(next)) return;
                      this.setState({ size: next });
                      this.clearError('default_download_size');
                    }}
                  />
                  <FieldErrors messages={errors.default_download_size} />
                </div>
              </div>

              <div className="qr-section-head">
                <div className="qr-section-label qr-section-label--inline">Style</div>
                <div className="btn-group qr-tabs" role="tablist">
                  {TABS.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === t.key}
                      className={`btn btn-default btn-sm${activeTab === t.key ? ' active' : ''}`}
                      onClick={() => this.setState({ activeTab: t.key })}
                    >
                      {t.label}
                      {errorLocations.has(t.key) && (
                        <span className="qr-tab-badge" aria-label="has errors" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="qr-card">
                <div className={panel('colors')}>
                  <ColorsTab
                    style={style}
                    patchStyle={this.patchStyle}
                    gradientTypes={p.gradient_types}
                    errors={errors}
                  />
                </div>
                <div className={panel('shapes')}>
                  <ShapesTab
                    style={style}
                    patchStyle={this.patchStyle}
                    moduleShapes={p.module_shapes}
                    eyeFrameShapes={p.eye_frame_shapes}
                    eyeBallShapes={p.eye_ball_shapes}
                    swatches={p.swatches}
                    errors={errors}
                  />
                </div>
                <div className={panel('logo')}>
                  <LogoTab
                    style={style}
                    patchStyle={this.patchStyle}
                    logoClips={p.logo_clips}
                    onLogoFile={this.handleLogoFile}
                    uploadError={logoUploadError}
                    errors={errors}
                    logoFilename={p.logo_filename}
                    pickedFileName={logoFileName}
                    removeLogo={removeLogo}
                    onClearLogo={this.handleClearLogo}
                    onRemoveLogo={this.handleRemoveLogo}
                  />
                </div>
              </div>

              {styleHiddenInputs(style)}
              {logoSignedId && (
                <input type="hidden" name="qr_code_template[logo]" value={logoSignedId} readOnly />
              )}
              {removeLogo && (
                <input type="hidden" name="qr_code_template[remove_logo]" value="1" readOnly />
              )}

              <div className="qr-actions">
                {p.cancel_url && <a href={p.cancel_url} className="btn btn-default">Cancel</a>}
                <button type="submit" className="btn btn-primary">
                  {p.template_id ? 'Update style' : 'Create style'}
                </button>
              </div>
            </form>
          </div>

          <div className="qr-preview-col">
            <QrPreview
              previewUrl={p.preview_url}
              style={style}
              size={size}
              templateId={p.template_id}
              logoSignedId={logoSignedId}
              uploadingLogo={uploadingLogo}
              logoRemoved={removeLogo}
            />
          </div>
        </div>
      </div>
    );
  }
}

QrStyleForm.propTypes = {
  initial_style: PropTypes.object.isRequired,
  name: PropTypes.string,
  default_download_size: PropTypes.number,
  template_id: PropTypes.number,
  submit_url: PropTypes.string.isRequired,
  cancel_url: PropTypes.string,
  http_method: PropTypes.oneOf(['post', 'patch']).isRequired,
  csrf: PropTypes.objectOf(PropTypes.string).isRequired,
  swatches: PropTypes.object.isRequired,
  preview_url: PropTypes.string.isRequired,
  sizes: PropTypes.arrayOf(PropTypes.number).isRequired,
  module_shapes: PropTypes.arrayOf(PropTypes.string).isRequired,
  eye_frame_shapes: PropTypes.arrayOf(PropTypes.string).isRequired,
  eye_ball_shapes: PropTypes.arrayOf(PropTypes.string).isRequired,
  logo_clips: PropTypes.arrayOf(PropTypes.string).isRequired,
  gradient_types: PropTypes.arrayOf(PropTypes.string).isRequired,
  logo_upload_url: PropTypes.string.isRequired,
  logo_filename: PropTypes.string,
  initial_errors: PropTypes.object,
};

QrStyleForm.defaultProps = {
  name: '',
  default_download_size: null,
  template_id: null,
  cancel_url: null,
  initial_errors: {},
  logo_filename: null,
};

export default QrStyleForm;
